import { ChangeDetectorRef, Component, Input, Optional } from "@angular/core";
import { AbstractValueAccessorComponent } from "../abstract-value-accessor.component";
import { ValueAccessorUtil } from "../value-accessor.util";
import { ControlContainer } from "@angular/forms";

enum InputSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export type InputSizeType = keyof typeof InputSize;

@Component({
  selector: "remainmybox-form-input",
  templateUrl: "./form-input.component.html",
  styleUrls: ["./form-input.component.scss"],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(FormInputComponent)]
})
export class FormInputComponent extends AbstractValueAccessorComponent<string | number> {
  @Input() public inputSize: InputSizeType = "large";
  @Input() public hasCopyIcon = false;
  @Input() public hasPencilIcon = false;

  private baseIconClasses = 'flex items-center justify-center relative mr-2 top-1/2 transform -translate-y-1/2';

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() protected override _controlContainer: ControlContainer
  ) {
    super(changeDetectorRef, _controlContainer);
  }

  inputChanged(): void {
    this.onChange(this.value);
    this.formControl?.setValue(this.value);
    this.changed.emit(this.value);
  }

  onKeyup(event: any) {
    this.onChange(event.target.value);
    this.formControl?.setValue(event.target.value);
    this.changed.emit(event.target.value);
  }

  get inputClasses(): string {
    let classes = 'mt-0.5 bg-white caret-blue focus:outline-blue focus:outline-0 disabled:outline-dashed disabled:outline-1 disabled:border-0 disabled:outline-gray-light border focus:border-blue text-black font-text-m-semibold text-sm rounded pb-0.2 block h-9 dark:bg-gray dark:border-gray dark:placeholder-gray dark:text-white dark:focus:ring-blue dark:focus:border-blue hover:border-blue focus:border-blue active:border-blue pl-3 ';
    classes = this.handleRequired(classes);
    classes = this.handleInputSize(classes);
    classes = this.handleErrors(classes);
    return classes;
  }

  get errorIconClasses(): string {
    let classes = `${this.baseIconClasses} ph ph-exclamation-mark text-white bg-red w-4 h-4 rounded-full bottom-[17px]`;
    classes = this.handleIconPositions(classes);
    return classes;
  }

  get copyIconClasses(): string {
    let classes = `${this.baseIconClasses} ph ph-copy-simple text-blue text-xl bg-transparent font-normal`;
    classes = this.handleIconPositions(classes);
    return classes;
  }

  get pencilIconClasses(): string {
    let classes = `${this.baseIconClasses} ph ph-pencil text-blue text-xl bg-transparent font-normal`;
    classes = this.handleIconPositions(classes);
    return classes;
  }

  private handleRequired(classes: string): string {
    if (this.required) {
      classes += ' bg-yellow-light border-yellow';
    } else {
      classes += ' border-gray-light';
    }
    return classes;
  }

  private handleInputSize(classes: string): string {
    if (this.inputSize === InputSize.small) {
      classes += ' w-[70px]';
    } else if (this.inputSize === InputSize.medium) {
      classes += ' w-[100px]';
    } else if (this.inputSize === InputSize.large) {
      classes += ' w-[150px]';
    }
    return classes;
  }

  private handleIconPositions(classes: string): string {
    if ((this.hasCopyIcon || this.hasPencilIcon) && this.control?.errors && this.control.touched) {
      classes = this.handleIconPositionsIfAllIconsPresent(classes);
    }
    if (this.inputSize === InputSize.small) {
      classes += ' left-[45px]';
    } else if (this.inputSize === InputSize.medium) {
      classes += ' left-[75px]';
    } else if (this.inputSize === InputSize.large) {
      classes += ' left-[125px]';
    }
    return classes;
  }

  private handleIconPositionsIfAllIconsPresent(classes: string): string {
    if (classes.includes('ph-copy-simple') || classes.includes('ph-pencil')) {
      classes += ' right-0';
    } else if (classes.includes('ph-exclamation-mark')) {
      if (this.inputSize === InputSize.small) {
        classes += ' left-24';
      } else if (this.inputSize === InputSize.medium) {
        classes += ' right-40 mr-4';
      } else if (this.inputSize === InputSize.large) {
        classes += ' right-6';
      }
    }
    return classes;
  }

  private handleErrors(classes: string): string {
    if (this.control?.errors && this.control.touched) {
      if (classes.includes('border-yellow')) {
        classes = classes.replace('border-yellow', 'border-red');
      } else {
        classes += ' border-red';
      }
    }
    return classes;
  }
}
